import React, { useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Collapse, Box, Typography
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { green, red } from '@mui/material/colors';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

type Passenger = {
  name: string;
  gender: string;
  ticketNumber: string;
};

type RowData = {
  pnr: string;
  officeId: string;
  supplier: string;
  from: string;
  to: string;
  flightNumber: string;
  date: string;
  passengers: Passenger[];
  isPaymentDone: boolean;
};

const rows: RowData[] = [
  {
    pnr: 'ABC123',
    officeId: 'OFF001',
    supplier: 'Amadeus',
    from: 'JFK',
    to: 'LAX',
    flightNumber: 'AA100',
    date: '2025-06-15',
    passengers: [
      { name: 'John Doe', gender: 'Male', ticketNumber: 'TK123456' },
      { name: 'Jane Smith', gender: 'Female', ticketNumber: 'TK654321' }
    ],
    isPaymentDone: true,
  },
  {
    pnr: 'XYZ789',
    officeId: 'OFF002',
    supplier: 'Sabre',
    from: 'DEL',
    to: 'DXB',
    flightNumber: 'EK501',
    date: '2025-07-01',
    passengers: [
      { name: 'Ali Khan', gender: 'Male', ticketNumber: 'TK789012' }
    ],
    isPaymentDone: false,
  }
];

interface RowProps {
  row: RowData;
}

function Row({ row }: RowProps) {
  const [open, setOpen] = useState(false);

  const renderPaymentStatus = (isDone: boolean) => (
    <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
      {isDone ? (
        <CheckCircleIcon fontSize="small" sx={{ color: green[600] }} />
      ) : (
        <CancelIcon fontSize="small" sx={{ color: red[600] }} />
      )}
    </Box>
  );

  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
            className="viewBtn"
          >
            {open ? (
              <>
                Passenger Details <KeyboardArrowUpIcon />
              </>
            ) : (
              <>
                Passenger Details <KeyboardArrowDownIcon />
              </>
            )}
          </IconButton>
        </TableCell>
        <TableCell>{row.pnr}</TableCell>
        <TableCell>{row.officeId}</TableCell>
        <TableCell>{row.supplier}</TableCell>
        <TableCell>{row.from}</TableCell>
        <TableCell>{row.to}</TableCell>
        <TableCell>{row.flightNumber}</TableCell>
        <TableCell>{row.date}</TableCell>
        <TableCell align="center">{renderPaymentStatus(row.isPaymentDone)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={2}>
              <Table size="small" aria-label="passengers">
                <TableHead>
                  <TableRow>
                    <TableCell>Full Name</TableCell>
                    <TableCell>Gender</TableCell>
                    <TableCell>Ticket Number</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {row.passengers.map((p, index) => (
                    <TableRow key={index}>
                      <TableCell>{p.name}</TableCell>
                      <TableCell>{p.gender}</TableCell>
                      <TableCell>{p.ticketNumber}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function MIS() {
  const [fromDate, setFromDate] = useState<dayjs.Dayjs | null>(null);
  const [toDate, setToDate] = useState<dayjs.Dayjs | null>(null);

  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const handleDateChange = (newValue: dayjs.Dayjs | null) => {
    setToDate(newValue);
    setToOpen(false); // Close the calendar after selecting a date
  };

  return (
    <>
      <Box className="headingBtnWrap">
        <Typography variant="h1">MIS</Typography>
      </Box>

      <Box className="modalForm" marginBottom={4}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Box className="inputFieldWrap">
            <label>From</label>
            <div onClick={() => setFromOpen(true)}>
            <DatePicker
              open={fromOpen}
              onOpen={() => setFromOpen(true)}
              onClose={() => setFromOpen(false)}
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
              className='datePickerWrap'
            />
            </div>
          </Box>
          <Box className="inputFieldWrap">
            <label>To</label>
            <div onClick={() => setToOpen(true)}>
            <DatePicker
              open={toOpen}
              // onOpen={() => setToOpen(true)}
              onClose={() => setToOpen(false)}
              value={toDate}
              onChange={handleDateChange}
              slotProps={{ textField: { fullWidth: true } }}
              className='datePickerWrap'
            />
            </div>
          </Box>
        </LocalizationProvider>
      </Box>

      <TableContainer component={Paper}>
        <Table aria-label="PNR table">
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>PNR Number</TableCell>
              <TableCell>Office ID</TableCell>
              <TableCell>Supplier</TableCell>
              <TableCell>From</TableCell>
              <TableCell>To</TableCell>
              <TableCell>Flight Number</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Payment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <Row key={index} row={row} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
